import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/apiClient';
import type { DraftEnvelope, ThemeTemplate } from '@/types/sections';
import { useSectionsDraft } from '@/queries/sections';
import { usePreviewStore } from '@/state/previewStore';
import { useEditorStore } from '@/state/editorStore';
import { useSelectionStore } from '@/state/selectionStore';

// ── Canonical index draft ─────────────────────────────────────────────────
// `globalSettings` is conceptually global (Sticky ATC, colors, animations,
// nav, typography, …) but historically lives inside per-template drafts.
// Per-template copies drift over time, so when the merchant previews a
// product/collection page they used to see STALE `globalSettings` — even
// after editing them under Animations. Fix: every per-template staged blob
// is hydrated with the index draft's `globalSettings`, so the form and
// the preview agree regardless of which page the preview is showing.
// (When the active template IS index, this is a no-op — same source.)
function useIndexDraft() {
  return useQuery({
    queryKey: ['sections', 'draft', 'index'],
    queryFn: () =>
      apiRequest<DraftEnvelope>('/api/sections/draft?template=index'),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

interface SaveResponse {
  version_id: number;
  parent_id: number | null;
}

/**
 * Owns hydration of the staged working copy from the server draft. Mount
 * exactly once (EditorShell) — every panel reads the staged copy, but only
 * one place should adopt fresh server data into it.
 *
 * For non-index templates we wait for the canonical index draft so the
 * staged blob is seeded with index `globalSettings` instead of whatever
 * stale copy lives in `drafts/<template>.json` — see `useIndexDraft` above.
 */
export function useHydrateStagedDraft() {
  const draft = useSectionsDraft();
  const indexDraft = useIndexDraft();
  const template = useSelectionStore((s) => s.template);
  const hydrate = useEditorStore((s) => s.hydrate);

  useEffect(() => {
    const json = draft.data?.json_blob;
    if (!json) return;
    if (template === 'index') {
      // Index IS the canonical source; no overlay needed.
      hydrate(template, json);
      return;
    }
    // Non-index: wait for the index draft so we can overlay its globals.
    const indexGlobals = indexDraft.data?.json_blob?.globalSettings;
    if (!indexGlobals) return;
    hydrate(template, { ...json, globalSettings: indexGlobals });
  }, [draft.data, indexDraft.data, template, hydrate]);
}

/**
 * Read-only view of the effective template: the staged working copy when
 * it matches the active template, otherwise the server draft (while the
 * very first hydration is still in flight).
 */
export function useStagedDraft() {
  const draft = useSectionsDraft();
  const template = useSelectionStore((s) => s.template);
  const staged = useEditorStore((s) => s.staged);
  const stagedTemplate = useEditorStore((s) => s.stagedTemplate);

  const effective =
    staged && stagedTemplate === template
      ? staged
      : (draft.data?.json_blob ?? null);

  return {
    template: effective,
    isLoading: draft.isLoading,
    error: draft.error,
  };
}

/**
 * Flush the staged copy to the server. Reference-equality race guard: if
 * the user edits mid-flight, `staged` is a new object so we keep `dirty`
 * true instead of falsely marking the (now stale) snapshot as saved.
 */
export function useSaveStaged() {
  const qc = useQueryClient();
  const refreshPreview = usePreviewStore((s) => s.refresh);
  const template = useSelectionStore((s) => s.template);

  return useMutation({
    mutationFn: async () => {
      const { staged, setSaveStatus } = useEditorStore.getState();
      if (!staged) throw new Error('Nothing to save');
      setSaveStatus('saving');
      const snapshot = staged;
      const draftKey = ['sections', 'draft', template];
      const current = qc.getQueryData<DraftEnvelope>(draftKey);
      const result = await apiRequest<SaveResponse>('/api/sections/save', {
        method: 'POST',
        body: {
          template,
          json_blob: snapshot,
          parent_version_id: current?.version_id ?? null,
        },
      });
      return { result, snapshot, template };
    },
    onSuccess: ({
      result,
      snapshot,
      template: t,
    }: {
      result: SaveResponse;
      snapshot: ThemeTemplate;
      template: string;
    }) => {
      qc.setQueryData<DraftEnvelope>(['sections', 'draft', t], {
        version_id: result.version_id,
        parent_id: result.parent_id,
        json_blob: snapshot,
        updated_at: Date.now(),
      });
      const store = useEditorStore.getState();
      if (store.staged === snapshot) store.markSaved();
      else store.setSaveStatus('idle');
      refreshPreview();
      if (t !== 'index') {
        qc.invalidateQueries({ queryKey: ['sections', 'draft', 'index'] });
      }
    },
    onError: () => useEditorStore.getState().setSaveStatus('error'),
  });
}

/**
 * Drop staged edits and re-adopt the server draft. `discard()` alone would
 * leave `staged` null (applyEdit becomes a no-op until the next hydrate,
 * which has no trigger), so we immediately re-hydrate from the cached
 * server draft to keep the editor live.
 */
export function useDiscardStaged() {
  const qc = useQueryClient();
  const template = useSelectionStore((s) => s.template);
  const discard = useEditorStore((s) => s.discard);
  const hydrate = useEditorStore((s) => s.hydrate);

  return () => {
    discard();
    const env = qc.getQueryData<DraftEnvelope>(['sections', 'draft', template]);
    if (env?.json_blob) hydrate(template, env.json_blob);
  };
}

/**
 * Switch the active page-template, guarding unsaved staged edits with a
 * confirm (decision: never silently drop the merchant's work).
 */
export function requestTemplateSwitch(next: string) {
  const { dirty, stagedTemplate, discard } = useEditorStore.getState();
  if (
    dirty &&
    stagedTemplate &&
    stagedTemplate !== next &&
    !window.confirm('Discard unsaved changes?')
  ) {
    return;
  }
  discard();
  useSelectionStore.getState().setTemplate(next);
}
